import { useEffect, useRef, useState } from "react";

export type EmbedStatus = "loading" | "ready" | "failed";

type Options = {
  /** Full URL of the `embed-widget-*.js` bundle. */
  src: string;
  /** Widget config; serialized into the script tag the embed reads on load. */
  config: Record<string, unknown>;
  /** Applied to each generated frame. Frames stack, so position them. */
  frameClassName?: string;
};

/**
 * Mounts one of TradingView's external embed widgets into a host element.
 *
 * These embeds are not APIs: the script reads its config from its own
 * `innerHTML`, then replaces itself with an iframe inside its parent. There is
 * no handle and no update path, so changing the config means building a new
 * script — which is why the config is serialized and used as the effect dep.
 *
 * Teardown is the subtle part. The embed dereferences its own parent when it
 * loads, so detaching a script that is still in flight makes it throw. Instead
 * each run gets its own frame: a superseded frame is hidden at once and dropped
 * only after its script has settled, so nothing is ever removed mid-load.
 */
export function useTradingViewEmbed({ src, config, frameClassName }: Options) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<EmbedStatus>("loading");
  const serializedConfig = JSON.stringify(config);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    setStatus("loading");

    const frame = document.createElement("div");
    frame.className = frameClassName ? `tradingview-widget-container ${frameClassName}` : "tradingview-widget-container";

    const widget = document.createElement("div");
    widget.className = "tradingview-widget-container__widget";
    widget.style.height = "100%";
    frame.appendChild(widget);

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.innerHTML = serializedConfig;

    let settled = false;
    let superseded = false;

    /* `onload` only proves the bundle arrived — the chart paints a beat later,
       so callers keep their placeholder over the frame until then. An ad blocker
       or an offline visitor lands on `failed` instead. */
    const settle = (next: EmbedStatus) => {
      settled = true;

      if (superseded) {
        frame.remove();
        return;
      }

      setStatus(next);
    };

    script.onload = () => settle("ready");
    script.onerror = () => settle("failed");
    frame.appendChild(script);
    host.appendChild(frame);

    return () => {
      superseded = true;
      frame.style.display = "none";

      if (settled) {
        frame.remove();
      }
    };
  }, [src, serializedConfig, frameClassName]);

  return { hostRef, status };
}
